import React from 'react';
import { ModalHeader } from 'app/pages/guides/modals/VisibilityModal';
import { Form, Formik, FormikHelpers } from 'formik';
import { LoadingIndicator, NewModal } from 'lib/components';
import { Flex } from 'lib/components/styles/layout';
import { Button, Typography } from 'react-covideo-common';
import { IoArrowForward } from 'react-icons/io5';
import { MdAdd, MdClose } from 'react-icons/md';
import { BackgroundList } from 'app/pages/ai-configuration/aiAssistants/backgrounds/BackgroundList';
import { ICreateSpotlightFormikValues } from '../types';
import { useTheme } from 'styled-components/macro';
import { IBackgroundResponse } from 'lib/api/backgrounds/useQueryBackgrounds';
import { SCRIPT_MAX_LENGTH, SpotlightSchema } from './schema';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';

interface IAISpotlightFormProps {
  onSave: (
    values: ICreateSpotlightFormikValues,
    { setFieldValue }: FormikHelpers<ICreateSpotlightFormikValues>
  ) => Promise<void>;
  onClose: () => void;
  backgrounds: IBackgroundResponse[];
  toggleShowBackgroundUploadModal: () => void;
  isLoading: boolean;
  isFetchingBackgrounds: boolean;
  defaultBackgroundId: number;
}

const initialValues: ICreateSpotlightFormikValues = {
  text: '',
  backgroundId: 0,
};

export const AISpotlightForm = ({
  onSave,
  onClose,
  backgrounds,
  toggleShowBackgroundUploadModal,
  isLoading,
  isFetchingBackgrounds,
  defaultBackgroundId,
}: IAISpotlightFormProps) => {
  const theme = useTheme();

  return (
    <NewModal
      hideHeader
      closeModal={onClose}
      style={{
        content: {
          width: '100vw',
          maxHeight: '100vh',
          maxWidth: '100%',
          padding: 0,
        },
      }}
    >
      {isLoading ? (
        <Flex
          width='100%'
          height='100vh'
          justifyContent='center'
          alignItems='center'
        >
          <LoadingIndicator isLoading />
        </Flex>
      ) : (
        <Formik
          initialValues={{
            ...initialValues,
            backgroundId: defaultBackgroundId,
          }}
          onSubmit={onSave}
          validationSchema={SpotlightSchema}
          validateOnMount
        >
          {({ isSubmitting, setFieldValue, handleSubmit, values, isValid }) => {
            const onDefaultBackgroundChange = (backgroundId: number) => {
              setFieldValue('backgroundId', backgroundId);
            };

            return (
              <Form
                onSubmit={handleSubmit}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ModalHeader>
                  <Button
                    variant='transparent'
                    icon={<MdClose size='18px' />}
                    onClick={onClose}
                    disabled={
                      isSubmitting || isLoading || isFetchingBackgrounds
                    }
                  />
                  <Typography
                    fontWeight={600}
                    variant='subheading'
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Create AI Spotlight
                  </Typography>
                  <Button
                    type='submit'
                    variant='primary'
                    text='Next'
                    icon={<IoArrowForward />}
                    iconPosition='right'
                    onClick={() => handleSubmit()}
                    disabled={
                      isSubmitting || isLoading || !isValid || isLoading
                    }
                  />
                </ModalHeader>

                {/* Spotlight Settings Header */}
                <Flex
                  justifyContent='center'
                  alignItems='center'
                  style={{
                    background: theme.colors.neutral[20],
                    position: 'relative',
                    height: '48px',
                    minHeight: '48px',
                  }}
                >
                  <Typography fontWeight={600} color={theme.colors.black[80]}>
                    Spotlight Settings
                  </Typography>
                </Flex>

                {/* Form Body */}
                <Flex
                  justifyContent='flex-start'
                  alignItems='center'
                  padding='24px 0px'
                  style={{ overflowY: 'scroll' }}
                >
                  <Flex style={{ maxWidth: '650px' }} gap='33px'>
                    <Flex gap='16px'>
                      <Typography variant='subheading' fontWeight={600}>
                        AI Spotlight Script
                      </Typography>
                      <FormikTextAreaField
                        name='text'
                        placeholder='Start typing your AI Spotlight script...'
                        label=''
                        isRequired={false}
                        extendStyles={{
                          textAreaStyles: { minHeight: '212px' },
                        }}
                        showCounter
                        maxLength={SCRIPT_MAX_LENGTH}
                        disabled={
                          isSubmitting || isLoading || isFetchingBackgrounds
                        }
                      />
                    </Flex>

                    <Flex
                      justifyContent='space-between'
                      flexDirection='row'
                      alignItems='center'
                      width='100%'
                    >
                      <Typography variant='subheading' fontWeight={600}>
                        AI Spotlight Background
                      </Typography>
                      <Button
                        text='Add Background'
                        icon={<MdAdd color={theme.colors.white[100]} />}
                        onClick={toggleShowBackgroundUploadModal}
                        disabled={isLoading || isFetchingBackgrounds}
                      />
                    </Flex>

                    <BackgroundList
                      backgrounds={backgrounds || []}
                      selectedBackground={values.backgroundId}
                      isLoading={isFetchingBackgrounds}
                      onDefaultBackgroundChange={onDefaultBackgroundChange}
                      selectedText='Selected'
                      unselectedText='Select'
                      extendStyles={{
                        card: { cardWrapperStyles: { minWidth: '311px' } },
                        list: { wrapperStyles: { boxSizing: 'border-box' } },
                      }}
                    />
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      )}
    </NewModal>
  );
};
